import type {
  ChatAttachmentErrorAction,
  ChatAttachmentErrorCode,
  ChatAttachmentFailure
} from '@atbash-labs/ontology-agent-wire'

import { createOntologyHeaders } from '../api/core'

export interface ConsumeSSEOptions<T> {
  /** Absolute URL to POST to. */
  url: string
  /**
   * Request body. When a `FormData` instance is supplied the request is sent
   * as multipart/form-data (the browser fills in the boundary header and we
   * skip the JSON serializer); anything else is JSON-encoded.
   */
  body: unknown
  /** Aborts the in-flight fetch and the read loop when fired. */
  signal?: AbortSignal
  /** Called once per parsed event in the order the server emits them. */
  onEvent: (event: T) => void
}

const SSE_FRAME_SEPARATOR = '\n\n'
const DATA_PREFIX = 'data: '
const EVENT_PREFIX = 'event: '
const DONE_EVENT_NAME = 'done'
const MAX_ERROR_BODY_BYTES = 64 * 1024
const ATTACHMENT_ERROR_CODES = new Set<ChatAttachmentErrorCode>([
  'ATTACHMENT_EMPTY',
  'ATTACHMENT_FILE_TOO_LARGE',
  'ATTACHMENT_TEXT_TOO_LARGE',
  'ATTACHMENT_IMAGE_TOO_LARGE',
  'ATTACHMENT_PDF_TOO_LARGE',
  'ATTACHMENT_TOTAL_TOO_LARGE',
  'ATTACHMENT_TYPE_UNSUPPORTED',
  'ATTACHMENT_MIME_MISMATCH',
  'ATTACHMENT_MULTIPART_TOO_LARGE',
  'ATTACHMENT_TRANSFER_TIMEOUT',
  'ATTACHMENT_INGEST_REQUIRED',
  'ATTACHMENT_CLIENT_ID_REQUIRED',
  'ATTACHMENT_CLIENT_ID_DUPLICATE',
  'ATTACHMENT_STORAGE_UNAVAILABLE',
  'ATTACHMENT_UPLOAD_TIMEOUT',
  'ATTACHMENT_AUTHORIZATION_FAILED',
  'ATTACHMENT_OBJECT_WRITE_FAILED',
  'ATTACHMENT_METADATA_WRITE_FAILED',
  'ATTACHMENT_IDEMPOTENCY_CONFLICT',
  'ATTACHMENT_UPLOAD_IN_PROGRESS',
  'ATTACHMENT_DRAFT_EXPIRED',
  'ATTACHMENT_CLAIM_FAILED'
])
const ATTACHMENT_ERROR_ACTIONS = new Set<ChatAttachmentErrorAction>([
  'remove',
  'retry',
  'use_ingest'
])

export class SSERequestError extends Error {
  readonly status: number
  readonly attachmentFailures: ChatAttachmentFailure[]

  constructor(status: number, message: string, attachmentFailures: ChatAttachmentFailure[] = []) {
    super(message)
    this.name = 'SSERequestError'
    this.status = status
    this.attachmentFailures = attachmentFailures
  }
}

/**
 * POST `body` to `url` and stream the response back as Server-Sent Events.
 * Each frame's `data:` payload is JSON-parsed and handed to `onEvent` in the
 * order the server emits them. Resolves when the server closes the stream
 * (or sends the `event: done` terminator); rejects on non-2xx status, fetch
 * failure, or read errors. If `signal` aborts mid-stream the promise resolves
 * cleanly instead of rejecting with the abort error.
 *
 * The chunked-buffer split logic mirrors the proven pattern from the legacy
 * proxy-stream consumer: decode bytes into a rolling string buffer, slice on
 * `\n\n` until no more separators remain, and parse each completed frame.
 * Frames that span chunk boundaries get reassembled because the leftover tail
 * of `buffer` is preserved across `read()` calls.
 */
export async function consumeSSE<T>({
  url,
  body,
  signal,
  onEvent
}: ConsumeSSEOptions<T>): Promise<void> {
  // Multipart bodies need the browser-supplied boundary header — clear our
  // JSON content-type and send the FormData verbatim. Plain bodies stay on
  // the JSON path so existing callers don't change.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  const headers = createOntologyHeaders()
  if (isFormData && headers.has('content-type')) {
    headers.delete('content-type')
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: isFormData ? (body as FormData) : JSON.stringify(body),
    signal
  })

  if (!response.ok) {
    const parsed = await readSSEErrorResponse(response)
    throw new SSERequestError(
      response.status,
      parsed?.message ?? `SSE request failed with HTTP ${response.status}`,
      parsed?.attachments
    )
  }
  if (!response.body) {
    throw new Error('SSE response did not return a readable body.')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) buffer += decoder.decode(value, { stream: true })

      let separatorIdx = buffer.indexOf(SSE_FRAME_SEPARATOR)
      while (separatorIdx !== -1) {
        const frame = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + SSE_FRAME_SEPARATOR.length)
        dispatchFrame<T>(frame, onEvent)
        separatorIdx = buffer.indexOf(SSE_FRAME_SEPARATOR)
      }
    }
    // Flush any trailing partial frame the server didn't terminate with `\n\n`.
    if (buffer.length > 0) dispatchFrame<T>(buffer, onEvent)
  } catch (err) {
    if (signal?.aborted) return
    throw err
  } finally {
    reader.cancel().catch(() => undefined)
  }
}

async function readSSEErrorResponse(
  response: Response
): Promise<{ message: string; attachments: ChatAttachmentFailure[] } | null> {
  const text = await readBoundedResponseText(response, MAX_ERROR_BODY_BYTES)
  if (text === null || text.length === 0) return null
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>
    if (typeof parsed.message !== 'string') return null
    const attachments = Array.isArray(parsed.attachments)
      ? parsed.attachments.flatMap((failure) => {
          const normalized = normalizeAttachmentFailure(failure)
          return normalized === null ? [] : [normalized]
        })
      : []
    return { message: parsed.message, attachments }
  } catch {
    return null
  }
}

async function readBoundedResponseText(
  response: Response,
  maxBytes: number
): Promise<string | null> {
  if (response.body === null) return null
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let totalBytes = 0
  let text = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value === undefined) continue
      totalBytes += value.byteLength
      if (totalBytes > maxBytes) return null
      text += decoder.decode(value, { stream: true })
    }
    return text + decoder.decode()
  } catch {
    return null
  } finally {
    reader.cancel().catch(() => undefined)
  }
}

function normalizeAttachmentFailure(value: unknown): ChatAttachmentFailure | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const candidate = value as Record<string, unknown>
  if (
    !(
      candidate.index === null ||
      (Number.isSafeInteger(candidate.index) && (candidate.index as number) >= 0)
    ) ||
    typeof candidate.code !== 'string' ||
    !ATTACHMENT_ERROR_CODES.has(candidate.code as ChatAttachmentErrorCode) ||
    typeof candidate.message !== 'string' ||
    typeof candidate.retryable !== 'boolean' ||
    typeof candidate.action !== 'string' ||
    !ATTACHMENT_ERROR_ACTIONS.has(candidate.action as ChatAttachmentErrorAction)
  ) {
    return null
  }
  return {
    index: candidate.index as number | null,
    ...(typeof candidate.clientUploadId === 'string'
      ? { clientUploadId: candidate.clientUploadId }
      : {}),
    code: candidate.code as ChatAttachmentErrorCode,
    message: candidate.message,
    retryable: candidate.retryable,
    action: candidate.action as ChatAttachmentErrorAction
  }
}

/**
 * Parse one complete SSE frame and forward it. Skips the server-side
 * `event: done` terminator (its payload is `{}` and isn't a real event).
 * Multi-line `data:` payloads are joined with `\n` per the SSE spec.
 * Malformed JSON is silently dropped — the stream may legitimately contain
 * comments or other directives the consumer doesn't care about.
 */
function dispatchFrame<T>(frame: string, onEvent: (event: T) => void): void {
  const trimmed = frame.trim()
  if (!trimmed) return

  let isTerminator = false
  const dataLines: string[] = []

  for (const line of trimmed.split('\n')) {
    if (line.startsWith(EVENT_PREFIX)) {
      if (line.slice(EVENT_PREFIX.length).trim() === DONE_EVENT_NAME) {
        isTerminator = true
      }
    } else if (line.startsWith(DATA_PREFIX)) {
      dataLines.push(line.slice(DATA_PREFIX.length))
    }
  }

  if (isTerminator) return
  if (dataLines.length === 0) return

  const payload = dataLines.join('\n')
  if (!payload) return

  let parsed: T
  try {
    parsed = JSON.parse(payload) as T
  } catch {
    return
  }
  onEvent(parsed)
}
